import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { 
  Wrench, Droplets, Truck, ArrowLeft, Eye,
  LayoutDashboard, FileText, Package, Users, Settings,
  DollarSign, TrendingUp, Clock, AlertCircle, ShoppingCart,
  Barcode, Tags, Boxes, Receipt, Store, Globe, Ship,
  Building2, ClipboardList, Calculator, MapPin, Plus,
  ArrowUpRight, ArrowDownRight, Percent, Box, Search, Phone,
  Edit2, Trash2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useTheme } from "@/context/ThemeContext";
import GroceryStoreDemo from "@/components/GroceryStoreDemo";
import PressureWashingDemo from "@/components/PressureWashingDemo";

// Category configurations
const CATEGORY_CONFIG = {
  truck_repair: {
    name: "Mechanic Shop",
    icon: Wrench,
    color: "blue",
    description: "Auto repair, truck repair, service centers",
    status: "live",
    features: [
      { name: "Dashboard", icon: LayoutDashboard, description: "Overview of revenue, pending payments, and stats" },
      { name: "Invoices", icon: FileText, description: "Create and manage professional invoices" },
      { name: "Inventory", icon: Package, description: "Track parts, costs, and stock levels" },
      { name: "Customers", icon: Users, description: "Manage customer info and vehicle details" },
      { name: "Settings", icon: Settings, description: "Configure tax rates, payments, and more" }
    ],
    demoStats: { revenue: "$45,230", pending: "$12,450", invoices: 156, customers: 89 }
  },
  pressure_washing: {
    name: "Pressure Washing",
    icon: Droplets,
    color: "sky",
    description: "Pressure washing and soft washing services",
    status: "demo",
    features: [
      { name: "Dashboard", icon: LayoutDashboard, description: "Track jobs, revenue, and scheduling" },
      { name: "Estimates", icon: FileText, description: "Quick estimates for residential & commercial" },
      { name: "Services", icon: Package, description: "Manage service types and pricing" },
      { name: "Customers", icon: Users, description: "Residential and commercial clients" },
      { name: "Scheduling", icon: Clock, description: "Book and manage appointments" }
    ],
    demoStats: { revenue: "$28,500", pending: "$5,200", jobs: 234, customers: 156 }
  },
  load_board: {
    name: "Load Board",
    icon: Truck,
    color: "amber",
    description: "Trucking load board and freight matching",
    status: "demo",
    features: [
      { name: "Load Posting", icon: FileText, description: "Post and manage available loads" },
      { name: "Carrier Search", icon: Users, description: "Find and connect with carriers" },
      { name: "Rate Calculator", icon: Calculator, description: "Calculate shipping rates" },
      { name: "Trip Planning", icon: MapPin, description: "Plan routes and schedules" }
    ],
    demoStats: { activeLoads: 156, carriers: 89, avgRate: "$2.45/mi", completed: 1234 }
  },
  grocery_store: {
    name: "Grocery Store",
    icon: ShoppingCart,
    color: "emerald",
    description: "Grocery stores, supermarkets, markets",
    status: "demo",
    features: [
      { name: "Inventory", icon: Boxes, description: "Track stock with buy/sell prices" },
      { name: "POS System", icon: Receipt, description: "Point of sale with barcode scanning" },
      { name: "Profit Analysis", icon: TrendingUp, description: "Margins and profit tracking" },
      { name: "Suppliers", icon: Store, description: "Manage vendors and purchase orders" }
    ],
    demoStats: { revenue: "$89,450", profit: "$23,200", products: 2450, transactions: 1289 }
  },
  wholesale: {
    name: "Wholesale Import",
    icon: Globe,
    color: "slate",
    description: "Import/export and wholesale distribution",
    status: "demo",
    features: [
      { name: "Purchase Orders", icon: FileText, description: "Create and track POs to suppliers" },
      { name: "Shipment Tracking", icon: Ship, description: "Track containers from origin to warehouse" },
      { name: "Inventory", icon: Box, description: "Warehouse stock and landed costs" },
      { name: "Suppliers", icon: Building2, description: "Manage international suppliers" }
    ],
    demoStats: { ytdPurchases: "$187,400", inTransit: "$43,200", activeOrders: 8, suppliers: 12 }
  }
};

// GroceryStoreDemo is imported from @/components/GroceryStoreDemo.jsx

// PLACEHOLDER - This comment replaces the old GroceryStoreDemo code
// The actual component with POS/Register, Live Scan Feed, Barcode scanning,
// Inventory tracking, Expiry dates, and proper grocery store features
// is now in a separate file for better maintainability.
// WHOLESALE DEMO - ILKSOY's Import Dashboard (Buyer's Perspective)
const WholesaleDemo = ({ isDark }) => {
  const [activeTab, setActiveTab] = useState('dashboard');

  const pendingOrders = [
    { po: "PO-2025-0847", supplier: "YIWU SHINE IMPORT & EXPORT", product: "Roasted Sunflower Seeds", qty: "34,762 bags", amount: 23638.16, status: "In Transit", eta: "Jan 15, 2026", container: "CSNU7234521" },
    { po: "PO-2025-0832", supplier: "GUANGZHOU SNACK CO.", product: "Pumpkin Seeds Variety", qty: "18,500 bags", amount: 15240.00, status: "Customs", eta: "Jan 8, 2026", container: "MSCU8912345" },
    { po: "PO-2025-0819", supplier: "SHANGHAI FOODS LTD", product: "Dried Fruit Mix", qty: "12,000 packs", amount: 9600.00, status: "Loading", eta: "Jan 22, 2026", container: "PENDING" },
  ];

  const warehouseInventory = [
    { sku: "SF-001", product: "Roasted Sunflower Seeds 200g", landedCost: 0.72, sellPrice: 1.49, stock: 15420, warehouse: "MD Warehouse" },
    { sku: "SF-002", product: "Roasted Sunflower Seeds 400g", landedCost: 1.35, sellPrice: 2.79, stock: 8750, warehouse: "MD Warehouse" },
    { sku: "PS-001", product: "Pumpkin Seeds Original", landedCost: 0.85, sellPrice: 1.99, stock: 6200, warehouse: "MD Warehouse" },
    { sku: "DF-001", product: "Dried Apricots 250g", landedCost: 1.20, sellPrice: 2.49, stock: 3450, warehouse: "MD Warehouse" },
  ];

  const suppliers = [
    { name: "YIWU SHINE IMPORT & EXPORT CO., LTD", country: "China", ytdOrders: 8, ytdValue: 87200, rating: 5 },
    { name: "GUANGZHOU SNACK CO.", country: "China", ytdOrders: 5, ytdValue: 42500, rating: 4 },
    { name: "SHANGHAI FOODS LTD", country: "China", ytdOrders: 3, ytdValue: 28400, rating: 4 },
    { name: "TURKISH DRIED GOODS", country: "Turkey", ytdOrders: 2, ytdValue: 18500, rating: 5 },
  ];

  return (
    <div className="space-y-6">
      {/* Company Header */}
      <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-slate-200'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isDark ? 'bg-slate-700' : 'bg-slate-100'}`}>
              <Globe className={`w-6 h-6 ${isDark ? 'text-slate-300' : 'text-slate-600'}`} />
            </div>
            <div>
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>ILKSOY GROUP LLC</h2>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Woodstock, MD • Import & Distribution</p>
            </div>
          </div>
          <button className={`px-4 py-2 rounded-lg font-medium ${isDark ? 'bg-slate-700 text-white hover:bg-slate-600' : 'bg-slate-800 text-white hover:bg-slate-700'}`}>
            <Plus className="w-4 h-4 inline mr-2" /> New Purchase Order
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
          <CardContent className="p-4">
            <p className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>YTD Purchases</p>
            <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>$187,400</p>
          </CardContent>
        </Card>
        <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
          <CardContent className="p-4">
            <p className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>In Transit Value</p>
            <p className={`text-xl font-bold ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>$48,478</p>
          </CardContent>
        </Card>
        <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
          <CardContent className="p-4">
            <p className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Active Orders</p>
            <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>3</p>
          </CardContent>
        </Card>
        <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
          <CardContent className="p-4">
            <p className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Warehouse Value</p>
            <p className={`text-xl font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>$34,200</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-700 pb-2">
        {['dashboard', 'orders', 'inventory', 'suppliers'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
              activeTab === tab
                ? isDark ? 'bg-slate-800 text-white border-b-2 border-slate-400' : 'bg-white text-slate-800 border-b-2 border-slate-600'
                : isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Active Shipments */}
          <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
                <Ship className="w-5 h-5" /> Active Shipments
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {pendingOrders.filter(o => o.status !== 'Loading').map((order, idx) => (
                <div key={idx} className={`p-3 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>{order.product}</p>
                      <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{order.supplier}</p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      order.status === 'In Transit' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {order.status}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-1 text-xs">
                      <MapPin className={`w-3 h-3 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
                      <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>Container: {order.container}</span>
                    </div>
                    <span className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>ETA: {order.eta}</span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Top Suppliers */}
          <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
                <Building2 className="w-5 h-5" /> Top Suppliers
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {suppliers.slice(0, 3).map((supplier, idx) => (
                <div key={idx} className={`p-3 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-800'}`}>{supplier.name}</p>
                      <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{supplier.country} • {supplier.ytdOrders} orders</p>
                    </div>
                    <span className={`font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>${supplier.ytdValue.toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Orders Tab */}
      {activeTab === 'orders' && (
        <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className={isDark ? 'border-b border-slate-700 bg-slate-700/30' : 'border-b border-slate-200 bg-slate-50'}>
                  <th className={`text-left p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>PO #</th>
                  <th className={`text-left p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Supplier</th>
                  <th className={`text-left p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Product</th>
                  <th className={`text-right p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Amount</th>
                  <th className={`text-center p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Status</th>
                  <th className={`text-right p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>ETA</th>
                </tr>
              </thead>
              <tbody>
                {pendingOrders.map((order, idx) => (
                  <tr key={idx} className={isDark ? 'border-b border-slate-700/50' : 'border-b border-slate-100'}>
                    <td className={`p-3 font-mono text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{order.po}</td>
                    <td className={`p-3 text-sm ${isDark ? 'text-white' : 'text-slate-800'}`}>{order.supplier}</td>
                    <td className={`p-3 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                      <span className="block">{order.product}</span>
                      <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{order.qty}</span>
                    </td>
                    <td className={`p-3 text-right font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>${order.amount.toLocaleString()}</td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        order.status === 'In Transit' ? 'bg-blue-100 text-blue-700' : 
                        order.status === 'Customs' ? 'bg-amber-100 text-amber-700' : 
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {order.status}
                      </span>
                    </td>
                    <td className={`p-3 text-right text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{order.eta}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Inventory Tab */}
      {activeTab === 'inventory' && (
        <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
          <CardHeader>
            <CardTitle className={isDark ? 'text-white' : 'text-slate-800'}>Warehouse Inventory</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className={isDark ? 'border-b border-slate-700 bg-slate-700/30' : 'border-b border-slate-200 bg-slate-50'}>
                  <th className={`text-left p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>SKU</th>
                  <th className={`text-left p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Product</th>
                  <th className={`text-right p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Landed Cost</th>
                  <th className={`text-right p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Sell Price</th>
                  <th className={`text-right p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Margin</th>
                  <th className={`text-right p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Stock</th>
                </tr>
              </thead>
              <tbody>
                {warehouseInventory.map((item, idx) => (
                  <tr key={idx} className={isDark ? 'border-b border-slate-700/50' : 'border-b border-slate-100'}>
                    <td className={`p-3 font-mono text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{item.sku}</td>
                    <td className={`p-3 font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>{item.product}</td>
                    <td className={`p-3 text-right ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>${item.landedCost.toFixed(2)}</td>
                    <td className={`p-3 text-right font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>${item.sellPrice.toFixed(2)}</td>
                    <td className={`p-3 text-right font-bold text-emerald-500`}>{Math.round((1 - item.landedCost/item.sellPrice) * 100)}%</td>
                    <td className={`p-3 text-right font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>{item.stock.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Suppliers Tab */}
      {activeTab === 'suppliers' && (
        <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className={isDark ? 'border-b border-slate-700 bg-slate-700/30' : 'border-b border-slate-200 bg-slate-50'}>
                  <th className={`text-left p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Supplier</th>
                  <th className={`text-left p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Country</th>
                  <th className={`text-right p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>YTD Orders</th>
                  <th className={`text-right p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>YTD Value</th>
                </tr>
              </thead>
              <tbody>
                {suppliers.map((supplier, idx) => (
                  <tr key={idx} className={isDark ? 'border-b border-slate-700/50' : 'border-b border-slate-100'}>
                    <td className={`p-3 font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>{supplier.name}</td>
                    <td className={`p-3 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{supplier.country}</td>
                    <td className={`p-3 text-right ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{supplier.ytdOrders}</td>
                    <td className={`p-3 text-right font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>${supplier.ytdValue.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// LOAD BOARD DEMO - Links to external app with preview
const LoadBoardDemo = ({ isDark }) => {
  const sampleLoads = [
    { id: "MC-847291", origin: "Memphis, TN", dest: "Dallas, TX", miles: 453, rate: "$1,132", ratePerMile: "2.50", equipment: "Dry Van", weight: "44,200 lbs", length: "53 ft", partial: false, broker: "Landstar Express", phone: "(901) 555-0142", pickup: "02/11", delivery: "02/12" },
    { id: "MC-847305", origin: "Atlanta, GA", dest: "Charlotte, NC", miles: 245, rate: "$612", ratePerMile: "2.50", equipment: "Dry Van", weight: "18,500 lbs", length: "53 ft", partial: true, broker: "CH Robinson", phone: "(404) 555-0198", pickup: "02/11", delivery: "02/11" },
    { id: "MC-847318", origin: "Chicago, IL", dest: "Indianapolis, IN", miles: 182, rate: "$492", ratePerMile: "2.70", equipment: "Reefer", weight: "41,000 lbs", length: "53 ft", partial: false, broker: "TQL Logistics", phone: "(312) 555-0176", pickup: "02/11", delivery: "02/11" },
    { id: "MC-847324", origin: "Houston, TX", dest: "San Antonio, TX", miles: 197, rate: "$433", ratePerMile: "2.20", equipment: "Flatbed", weight: "22,400 lbs", length: "48 ft", partial: true, broker: "Coyote Logistics", phone: "(713) 555-0134", pickup: "02/12", delivery: "02/12" },
    { id: "MC-847336", origin: "Los Angeles, CA", dest: "Phoenix, AZ", miles: 372, rate: "$856", ratePerMile: "2.30", equipment: "Dry Van", weight: "38,750 lbs", length: "53 ft", partial: false, broker: "XPO Logistics", phone: "(213) 555-0167", pickup: "02/11", delivery: "02/12" },
    { id: "MC-847348", origin: "Newark, NJ", dest: "Boston, MA", miles: 215, rate: "$581", ratePerMile: "2.70", equipment: "Reefer", weight: "12,800 lbs", length: "48 ft", partial: true, broker: "Echo Global", phone: "(973) 555-0189", pickup: "02/12", delivery: "02/12" },
    { id: "MC-847359", origin: "Denver, CO", dest: "Salt Lake City, UT", miles: 525, rate: "$1,207", ratePerMile: "2.30", equipment: "Dry Van", weight: "42,100 lbs", length: "53 ft", partial: false, broker: "JB Hunt", phone: "(303) 555-0145", pickup: "02/11", delivery: "02/13" },
  ];

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
          <CardContent className="p-4">
            <p className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Active Loads</p>
            <p className={`text-xl font-bold ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>2,847</p>
          </CardContent>
        </Card>
        <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
          <CardContent className="p-4">
            <p className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Available Trucks</p>
            <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>1,234</p>
          </CardContent>
        </Card>
        <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
          <CardContent className="p-4">
            <p className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Avg Rate/Mile</p>
            <p className={`text-xl font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>$2.45</p>
          </CardContent>
        </Card>
        <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
          <CardContent className="p-4">
            <p className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Partial Loads</p>
            <p className={`text-xl font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>312</p>
          </CardContent>
        </Card>
      </div>

      {/* AI Negotiator Banner */}
      <div className={`p-4 rounded-lg border ${isDark ? 'bg-gradient-to-r from-purple-900/30 to-blue-900/30 border-purple-700' : 'bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-purple-800' : 'bg-purple-100'}`}>
              <Phone className={`w-5 h-5 ${isDark ? 'text-purple-300' : 'text-purple-600'}`} />
            </div>
            <div>
              <p className={`font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>AI Call Negotiator</p>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Let AI negotiate rates with brokers - average 8% better rates</p>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${isDark ? 'bg-purple-600 text-white' : 'bg-purple-100 text-purple-700'}`}>
            NEW
          </span>
        </div>
      </div>

      {/* Available Loads Table */}
      <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className={`text-lg ${isDark ? 'text-white' : 'text-slate-800'}`}>Available Loads</CardTitle>
            <div className="flex items-center gap-2">
              <span className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${isDark ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                <Box className="w-3 h-3" /> Partial Available
              </span>
              <a 
                href="https://shipment-mgmt-3.preview.emergentagent.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className={`px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-2 ${
                  isDark ? 'bg-amber-600 text-white hover:bg-amber-700' : 'bg-amber-500 text-white hover:bg-amber-600'
                }`}
              >
                <Truck className="w-4 h-4" /> Open Full Board
              </a>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={isDark ? 'border-b border-slate-700 bg-slate-700/30' : 'border-b border-slate-200 bg-slate-50'}>
                  <th className={`text-left p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Load</th>
                  <th className={`text-left p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Route</th>
                  <th className={`text-left p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Dates</th>
                  <th className={`text-center p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Equipment</th>
                  <th className={`text-center p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Length</th>
                  <th className={`text-right p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Miles</th>
                  <th className={`text-right p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Rate</th>
                  <th className={`text-center p-3 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Action</th>
                </tr>
              </thead>
              <tbody>
                {sampleLoads.map((load, idx) => (
                  <tr key={idx} className={`${isDark ? 'border-b border-slate-700/50 hover:bg-slate-700/30' : 'border-b border-slate-100 hover:bg-slate-50'} transition-colors`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <span className={`font-mono text-sm ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>{load.id}</span>
                        {load.partial && (
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${isDark ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                            LTL
                          </span>
                        )}
                      </div>
                      <p className={`text-xs mt-0.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{load.weight}</p>
                    </td>
                    <td className="p-3">
                      <p className={`text-sm ${isDark ? 'text-white' : 'text-slate-800'}`}>{load.origin}</p>
                      <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>→ {load.dest}</p>
                    </td>
                    <td className="p-3">
                      <p className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>PU: {load.pickup}</p>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>DEL: {load.delivery}</p>
                    </td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-1 rounded text-xs ${
                        load.equipment === 'Reefer' 
                          ? isDark ? 'bg-cyan-900/50 text-cyan-300' : 'bg-cyan-100 text-cyan-700'
                          : load.equipment === 'Flatbed'
                          ? isDark ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-100 text-orange-700'
                          : isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {load.equipment}
                      </span>
                    </td>
                    <td className={`p-3 text-center font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                      {load.length}
                    </td>
                    <td className={`p-3 text-right ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{load.miles}</td>
                    <td className="p-3 text-right">
                      <p className={`font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{load.rate}</p>
                      <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>${load.ratePerMile}/mi</p>
                    </td>
                    <td className="p-3 text-center">
                      <button 
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          isDark 
                            ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700' 
                            : 'bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:from-purple-600 hover:to-blue-600'
                        }`}
                        title={`AI will call ${load.broker} at ${load.phone}`}
                      >
                        <Phone className="w-3 h-3" />
                        AI Call
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Features */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: FileText, name: "Post Loads", desc: "List freight instantly" },
          { icon: Users, name: "Find Carriers", desc: "Verified truckers" },
          { icon: Phone, name: "AI Negotiator", desc: "Better rates, less calls" },
          { icon: MapPin, name: "Route Optimizer", desc: "Deadhead reduction" }
        ].map((feature, idx) => (
          <Card key={idx} className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
            <CardContent className="p-4 text-center">
              <feature.icon className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
              <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-800'}`}>{feature.name}</p>
              <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{feature.desc}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Generic Demo Dashboard
const GenericDemoDashboard = ({ category, config, isDark }) => {
  const stats = config.demoStats;
  
  return (
    <div className="space-y-6">
      {/* Demo Banner */}
      <div className={`p-4 rounded-lg border ${isDark ? 'bg-purple-900/20 border-purple-700 text-purple-300' : 'bg-purple-50 border-purple-200 text-purple-700'}`}>
        <div className="flex items-center gap-3">
          <Eye className="w-5 h-5" />
          <div>
            <p className="font-medium">Interactive Demo</p>
            <p className={`text-sm ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>Explore the features designed for {config.name.toLowerCase()} businesses</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.entries(stats).map(([key, value]) => (
          <Card key={key} className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
            <CardContent className="p-4">
              <p className={`text-xs font-medium capitalize ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{key.replace(/([A-Z])/g, ' $1')}</p>
              <p className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-slate-800'}`}>{value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Features */}
      <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
        <CardHeader>
          <CardTitle className={isDark ? 'text-white' : 'text-slate-800'}>Available Features</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {config.features.map((feature, idx) => (
              <div key={idx} className={`p-4 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                <feature.icon className={`w-6 h-6 mb-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
                <h4 className={`font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>{feature.name}</h4>
                <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{feature.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default function Demo() {
  const { category } = useParams();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  
  const config = CATEGORY_CONFIG[category];
  
  // Redirect truck_repair to login - it's live, not a demo
  if (category === 'truck_repair') {
    window.location.href = '/login';
    return null;
  }
  
  if (!config) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-slate-900' : 'bg-slate-100'}`}>
        <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
          <CardContent className="p-8 text-center">
            <p className={`text-lg ${isDark ? 'text-white' : 'text-slate-800'}`}>Category not found</p>
            <Link to="/login">
              <Button className="mt-4">Back to Login</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const Icon = config.icon;

  return (
    <div className={`min-h-screen ${isDark ? 'bg-slate-900' : 'bg-slate-100'}`}>
      {/* Header */}
      <div className={`sticky top-0 z-50 border-b ${isDark ? 'bg-slate-900/95 border-slate-700' : 'bg-white/95 border-slate-200'} backdrop-blur-sm`}>
        <div className="max-w-7xl mx-auto px-3 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4">
              <Link to="/demos">
                <Button variant="ghost" size="sm" className={`px-2 sm:px-3 ${isDark ? 'text-slate-300 hover:text-white' : ''}`}>
                  <ArrowLeft className="w-5 h-5 sm:mr-2" />
                  <span className="hidden sm:inline">Back</span>
                </Button>
              </Link>
              <div className="h-5 w-px bg-slate-600 hidden sm:block" />
              <div className="flex items-center gap-2">
                <Icon className={`w-5 h-5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`} />
                <span className={`font-semibold text-sm sm:text-base ${isDark ? 'text-white' : 'text-slate-800'}`}>{config.name}</span>
                <span className={`text-xs px-2 py-0.5 rounded hidden sm:inline ${isDark ? 'bg-slate-700 text-slate-400' : 'bg-slate-200 text-slate-500'}`}>Demo</span>
              </div>
            </div>
            <Link to="/signup">
              <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white text-xs sm:text-sm">Get Started</Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {category === 'grocery_store' && <GroceryStoreDemo isDark={isDark} />}
        {category === 'wholesale' && <WholesaleDemo isDark={isDark} />}
        {category === 'load_board' && <LoadBoardDemo isDark={isDark} />}
        {category === 'pressure_washing' && <PressureWashingDemo isDark={isDark} />}
        {category !== 'grocery_store' && category !== 'wholesale' && category !== 'load_board' && category !== 'pressure_washing' && (
          <GenericDemoDashboard category={category} config={config} isDark={isDark} />
        )}
      </div>
    </div>
  );
}
